import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { FaCode, FaMobileAlt, FaDatabase, FaCloud, FaCogs } from 'react-icons/fa';

const Services = () => {
  const { t } = useTranslation();

  const services = [
    {
      title: t('services.webDev.title'),
      description: t('services.webDev.description'),
      icon: <FaCode size={50} />,
      features: ['React', 'Node.js', 'Responsive Design']
    },
    {
      title: t('services.mobile.title'),
      description: t('services.mobile.description'),
      icon: <FaMobileAlt size={50} />,
      features: ['React Native', 'iOS', 'Android']
    },
    {
      title: t('services.database.title'),
      description: t('services.database.description'),
      icon: <FaDatabase size={50} />,
      features: ['SQL', 'MongoDB', 'Firebase']
    },
    {
      title: t('services.cloud.title'),
      description: t('services.cloud.description'),
      icon: <FaCloud size={50} />,
      features: ['AWS', 'Firebase', 'Cloud Hosting']
    },
    {
      title: t('services.automation.title'),
      description: t('services.automation.description'),
      icon: <FaCogs size={50} />,
      features: ['APIs', 'Workflows', 'Integrations']
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5
      }
    }
  };

  return (
    <section
      id="services"
      style={{
        background: '#ffffff',
        padding: '80px 20px',
        position: 'relative'
      }}
    >
      <div className="container" style={{ maxWidth: '1200px', margin: '0 auto' }}>
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-5"
        >
          {/* Barra superior */}
          <div
            style={{
              background: '#cc0033',
              color: 'white',
              padding: '8px 20px',
              fontSize: '0.85rem',
              fontWeight: '600',
              letterSpacing: '2px',
              textTransform: 'uppercase',
              marginBottom: '30px',
              display: 'inline-block'
            }}
          >
            {t('services.tag')}
          </div>

          <h2
            style={{
              fontSize: '2.5rem',
              fontWeight: '700',
              color: '#1a1a1a',
              marginBottom: '20px',
              fontFamily: 'Montserrat, sans-serif'
            }}
          >
            {t('services.title')}
          </h2>

          <p
            style={{
              fontSize: '1.05rem',
              color: '#555',
              marginBottom: '50px',
              maxWidth: '700px',
              margin: '0 auto'
            }}
          >
            {t('services.subtitle')}
          </p>
        </motion.div>

        {/* Grid de servicios */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '30px'
          }}
        >
          {services.map((service, index) => (
            <motion.div
              key={index}
              variants={itemVariants}
              whileHover={{ y: -10 }}
              transition={{ type: 'spring', stiffness: 300 }}
              style={{
                background: 'white',
                padding: '40px 30px',
                borderRadius: '12px',
                border: '2px solid #f0f0f0',
                textAlign: 'center',
                cursor: 'default',
                transition: 'all 0.3s ease',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = '#cc0033';
                e.currentTarget.style.boxShadow = '0 8px 24px rgba(204, 0, 51, 0.15)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = '#f0f0f0';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.05)';
              }}
            >
              {/* Icono */}
              <div
                style={{
                  width: '90px',
                  height: '90px',
                  margin: '0 auto 25px',
                  background: 'linear-gradient(135deg, #f5f5f5 0%, #e8e8e8 100%)',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#1a1a1a',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'linear-gradient(135deg, #cc0033 0%, #a60029 100%)';
                  e.currentTarget.style.color = 'white';
                  e.currentTarget.style.transform = 'scale(1.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'linear-gradient(135deg, #f5f5f5 0%, #e8e8e8 100%)';
                  e.currentTarget.style.color = '#1a1a1a';
                  e.currentTarget.style.transform = 'scale(1)';
                }}
              >
                {service.icon}
              </div>

              {/* Título */}
              <h5
                style={{
                  fontSize: '1.4rem',
                  fontWeight: '700',
                  color: '#1a1a1a',
                  marginBottom: '15px',
                  fontFamily: 'Montserrat, sans-serif'
                }}
              >
                {service.title}
              </h5>

              {/* Descripción */}
              <p
                style={{
                  fontSize: '0.95rem',
                  color: '#666',
                  lineHeight: '1.7',
                  marginBottom: '20px'
                }}
              >
                {service.description}
              </p>

              {/* Features */}
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '8px',
                  justifyContent: 'center'
                }}
              >
                {service.features.map((feature, idx) => (
                  <span
                    key={idx}
                    style={{
                      padding: '6px 14px',
                      fontSize: '0.75rem',
                      fontWeight: '600',
                      borderRadius: '20px',
                      background: '#f5f5f5',
                      color: '#333',
                      border: '1px solid #e0e0e0'
                    }}
                  >
                    {feature}
                  </span>
                ))}
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* CTA al final */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          viewport={{ once: true }}
          className="text-center mt-5"
          style={{ paddingTop: '40px' }}
        >
          <p
            style={{
              fontSize: '1.1rem',
              color: '#555',
              marginBottom: '25px'
            }}
          >
            {t('services.ctaQuestion')}
          </p>
          <motion.a
            href="#contact"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            style={{
              display: 'inline-block',
              padding: '14px 32px',
              background: 'linear-gradient(135deg, #cc0033 0%, #a60029 100%)',
              color: 'white',
              textDecoration: 'none',
              borderRadius: '8px',
              fontWeight: '600',
              fontSize: '1rem',
              boxShadow: '0 4px 15px rgba(204, 0, 51, 0.3)',
              transition: 'all 0.3s ease'
            }}
          >
            {t('services.ctaButton')}
          </motion.a>
        </motion.div>
      </div>
    </section>
  );
};

export default Services;